/**
 * Vectorize: a picture into an SVG, free, in the browser (Huanrui, 2026-10-09)
 * -------------------------------------------------------------
 * The board's Vectorize card (vectorNode.tsx). No model and no server: the
 * picture is traced by imagetracer.js (public domain, vendor/) in a worker,
 * and the SVG is kept in the project like any picture.
 *
 * Good for logos, line art, flat illustrations and sketches; a photograph
 * comes out as patches of colour, which is what tracing a photograph means.
 *
 * The picture is traced at no more than 1,600 pixels on its longer side:
 * past that, tracing takes much longer and the SVG gets much bigger for
 * little gain, and an SVG scales anyway.
 */

export type VectorStyle = 'colour' | 'mono'
export type VectorDetail = 'smooth' | 'balanced' | 'sharp'

export interface VectorSettings {
  style: VectorStyle
  /** How many colours, for 'colour'. 'mono' is always two. */
  colours: number
  detail: VectorDetail
}

export const VECTOR_COLOURS = [2, 4, 8, 16]
const LONGEST = 1600
const WAIT_MS = 120_000

function optionsFor(settings: VectorSettings): Record<string, unknown> {
  const detail =
    settings.detail === 'smooth'
      ? { ltres: 2, qtres: 2, pathomit: 16, blurradius: 2, blurdelta: 30 }
      : settings.detail === 'sharp'
        ? { ltres: 0.5, qtres: 0.5, pathomit: 4, blurradius: 0 }
        : { ltres: 1, qtres: 1, pathomit: 8, blurradius: 0 }
  return {
    ...detail,
    rightangleenhance: true,
    // Sampling the picture itself, without chance, so the same settings give the same result.
    colorsampling: settings.style === 'mono' ? 0 : 2,
    numberofcolors: settings.style === 'mono' ? 2 : Math.min(64, Math.max(2, Math.round(settings.colours))),
    colorquantcycles: 3,
    mincolorratio: 0,
    strokewidth: 1,
    roundcoords: 1,
    viewbox: true,
    desc: false,
    scale: 1,
  }
}

/** Black and white: each pixel by its lightness against the picture's own middle grey. */
function toMono(image: ImageData): void {
  const data = image.data
  let sum = 0
  let count = 0
  const light = new Float32Array(data.length / 4)
  for (let i = 0; i < light.length; i += 1) {
    const a = data[i * 4 + 3]! / 255
    // See-through counts as white paper.
    const l = (0.299 * data[i * 4]! + 0.587 * data[i * 4 + 1]! + 0.114 * data[i * 4 + 2]!) * a + 255 * (1 - a)
    light[i] = l
    sum += l
    count += 1
  }
  const middle = count ? sum / count : 128
  for (let i = 0; i < light.length; i += 1) {
    const value = light[i]! < middle ? 0 : 255
    data[i * 4] = value
    data[i * 4 + 1] = value
    data[i * 4 + 2] = value
    data[i * 4 + 3] = 255
  }
}

/** Trace a picture (a canvas at full size); answers the SVG and the size it is drawn at. */
export async function vectorize(
  source: HTMLCanvasElement,
  settings: VectorSettings,
): Promise<{ svg: string; width: number; height: number }> {
  const scale = Math.min(1, LONGEST / Math.max(source.width, source.height, 1))
  const width = Math.max(1, Math.round(source.width * scale))
  const height = Math.max(1, Math.round(source.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) throw new Error('This browser could not read the picture')
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, 0, 0, width, height)
  const image = context.getImageData(0, 0, width, height)
  if (settings.style === 'mono') toMono(image)

  const worker = new Worker(new URL('./vectorize.worker.ts', import.meta.url), { type: 'module' })
  try {
    const svg = await new Promise<string>((resolve, reject) => {
      const stop = setTimeout(() => reject(new Error('Tracing took too long — try fewer colours or Smooth')), WAIT_MS)
      worker.onmessage = (event: MessageEvent<{ svg?: string; error?: string }>) => {
        clearTimeout(stop)
        if (event.data.svg) resolve(event.data.svg)
        else reject(new Error(event.data.error || 'The picture could not be traced'))
      }
      worker.onerror = (event) => {
        clearTimeout(stop)
        reject(new Error(event.message || 'The picture could not be traced'))
      }
      worker.postMessage({ width, height, data: image.data, options: optionsFor(settings) }, [image.data.buffer])
    })
    // A size of its own as well as a viewBox, so it opens at the size it was traced at.
    return { svg: svg.replace('<svg ', `<svg width="${width}" height="${height}" `), width, height }
  } finally {
    worker.terminate()
  }
}
